import { MaterialSymbol } from 'react-material-symbols'
import type { SymbolCodepoints } from 'react-material-symbols'
import type { Blackout, BlackoutLevel } from '@lms/shared-types'
import { Card } from '../primitives/Card'
import { fmtDate, fmtDateRange } from '../../lib/format'
import type { EscalationTargets } from '../../api/blackouts'

const LEVEL_LABEL: Record<BlackoutLevel, string> = {
  head: 'Head of department',
  director: 'Director',
  exec_director: 'Executive Director',
}

interface BlackoutCardProps {
  blackout: Blackout
  teamsById: Map<string, string>
  escalationTargets: EscalationTargets | undefined
  onEdit: () => void
  onDelete: () => void
}

export function BlackoutCard({
  blackout,
  teamsById,
  escalationTargets,
  onEdit,
  onDelete,
}: BlackoutCardProps): JSX.Element {
  const teamNames =
    blackout.teamIds.length === 0
      ? 'All teams'
      : blackout.teamIds.map((id) => teamsById.get(id) ?? '—').join(', ')

  const escalationName = resolveEscalationName(blackout.level, escalationTargets)
  const createdLabel = fmtDate(blackout.createdAt)

  return (
    <Card
      padding={16}
      data-testid="blackout-row"
      data-blackout-id={blackout.id}
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      {/* Top row: name + icon buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>
          {blackout.name}
        </span>
        <IconButton
          icon="edit"
          label="Edit blackout"
          testId="edit-blackout"
          onClick={onEdit}
        />
        <IconButton
          icon="delete"
          label="Delete blackout"
          testId="delete-blackout"
          onClick={onDelete}
          danger
        />
      </div>

      {/* Second row: date chip + scope chip */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '2px 8px',
          }}
        >
          {fmtDateRange(blackout.startDate, blackout.endDate)}
        </span>
        <span
          style={{
            fontSize: 12,
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '2px 8px',
            color: 'var(--text-2)',
          }}
        >
          {teamNames}
        </span>
      </div>

      {/* Note (employee-visible) */}
      {blackout.note ? (
        <div
          style={{
            background: 'var(--surface-2)',
            padding: '8px 10px',
            borderRadius: 8,
            color: 'var(--text-2)',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {blackout.note}
        </div>
      ) : null}

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          fontSize: 12,
          color: 'var(--text-3)',
          marginTop: 2,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <MaterialSymbol icon="schedule" size={13} />
          Created {createdLabel}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <MaterialSymbol icon="shield" size={13} />
          {'Escalates to '}
          <strong style={{ color: 'var(--text)', fontWeight: 600 }}>
            {escalationName}
          </strong>
        </span>
      </div>
    </Card>
  )
}

function resolveEscalationName(
  level: BlackoutLevel,
  targets: EscalationTargets | undefined,
): string {
  if (!targets) return LEVEL_LABEL[level]
  const resolved =
    level === 'head'
      ? targets.head
      : level === 'director'
        ? targets.director
        : targets.execDirector
  return resolved ?? LEVEL_LABEL[level]
}

interface IconButtonProps {
  icon: SymbolCodepoints
  label: string
  testId: string
  onClick: () => void
  danger?: boolean
}

function IconButton({ icon, label, testId, onClick, danger = false }: IconButtonProps): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      data-testid={testId}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: 'var(--surface)',
        color: danger ? 'var(--danger-fg)' : 'var(--text-2)',
        cursor: 'pointer',
        flexShrink: 0,
        padding: 0,
      }}
    >
      <MaterialSymbol icon={icon} size={16} />
    </button>
  )
}
